import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckIcon,
  ExclamationCircleIcon,
  CreditCardIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";

// ─── Plans ────────────────────────────────────────────────────────
const PLANS = [
  { nombre: "Basic Rinse",   icon: "💧", precio: 9.99  },
  { nombre: "Express Wash",  icon: "🚿", precio: 19.99 },
  { nombre: "Full Detail",   icon: "✨", precio: 34.99 },
  { nombre: "Premium Shine", icon: "👑", precio: 49.99 },
];

// ─── Styles ───────────────────────────────────────────────────────
const inputCls = [
  "w-full pl-10 pr-4 py-2.5 text-[13px] bg-white border border-gray-200",
  "rounded-xl text-gray-800 placeholder-gray-400 outline-none",
  "focus:border-[#1a1f6c]/40 focus:ring-2 focus:ring-[#1a1f6c]/10",
  "transition-all duration-200",
].join(" ");

// ─── Helpers ──────────────────────────────────────────────────────
const formatCardNumber = (v) => {
  const digits = v.replace(/\D/g, "").slice(0, 16);
  return digits.replace(/(.{4})/g, "$1 ").trim();
};
const formatExpiry = (v) => {
  const digits = v.replace(/\D/g, "").slice(0, 4);
  if (digits.length >= 3) return digits.slice(0, 2) + "/" + digits.slice(2);
  return digits;
};

// ─── Animation variants ───────────────────────────────────────────
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
};

// ─── Field ────────────────────────────────────────────────────────
const Field = ({ label, icon, error, hint, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
      {label}
    </label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
        {icon}
      </span>
      {children}
    </div>
    <AnimatePresence>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="text-[11px] text-red-500 flex items-center gap-1"
        >
          <ExclamationCircleIcon className="w-3 h-3 flex-shrink-0" />
          {error}
        </motion.p>
      )}
    </AnimatePresence>
    {hint && !error && <p className="text-[10px] text-gray-400">{hint}</p>}
  </div>
);

// ─── Section Card ─────────────────────────────────────────────────
const SectionCard = ({ icon, title, sub, children }) => (
  <motion.div
    variants={fadeUp}
    className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm"
  >
    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2.5">
      <span className="text-[18px]">{icon}</span>
      <div>
        <p className="text-[13px] font-bold text-gray-800">{title}</p>
        {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
    <div className="px-5 py-5">{children}</div>
  </motion.div>
);

// ─── Spinner ──────────────────────────────────────────────────────
const Spinner = () => (
  <svg className="animate-spin w-4 h-4 text-white flex-shrink-0" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
  </svg>
);

const CarIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
      d="M8 17H16M5 17H4a1 1 0 01-1-1v-4l2-5h14l2 5v4a1 1 0 01-1 1h-1m-9 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
  </svg>
);

// ─── Main ─────────────────────────────────────────────────────────
// /join es una DEMO de ventas (la Pay de la demo del 15/05 enlazaba aquí con un QR):
// muestra el alta de membresía que tendrían los clientes del operador. No cobra ni
// envía nada; el aviso evita que un cliente real crea que ya se inscribió.
const DemoBanner = () => (
  <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-[12px] text-amber-800">
    <strong>Demo</strong> — no payment is taken. This page shows the membership signup your customers would get.
  </div>
);

export const JoinPage = () => {
  const [nombre,     setNombre]     = useState("");
  const [email,      setEmail]      = useState("");
  const [telefono,   setTelefono]   = useState("");
  const [placa,      setPlaca]      = useState("");
  const [planIdx,    setPlanIdx]    = useState(0);
  const [cardName,   setCardName]   = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry,     setExpiry]     = useState("");
  const [cvv,        setCvv]        = useState("");

  const [saving,     setSaving]     = useState(false);
  const [success,    setSuccess]    = useState(false);
  const [errors,     setErrors]     = useState({});
  const [shownPlan,  setShownPlan]  = useState(PLANS[0]);
  const [planVisible, setPlanVisible] = useState(true);
  const fadeTimer = useRef(null);

  useEffect(() => {
    if (fadeTimer.current) clearTimeout(fadeTimer.current);
    setPlanVisible(false);
    fadeTimer.current = setTimeout(() => {
      setShownPlan(PLANS[planIdx]);
      setPlanVisible(true);
    }, 130);
    return () => clearTimeout(fadeTimer.current);
  }, [planIdx]);

  const validate = () => {
    const e = {};
    if (!nombre.trim()) e.nombre = "Full name is required.";
    if (!email.trim())  e.email  = "Email is required.";
    else if (!/^\S+@\S+\.\S+$/.test(email)) e.email = "Enter a valid email.";
    if (placa.trim() && placa.trim().length < 3)
      e.placa = "License plate must be at least 3 characters.";
    if (!cardNumber || cardNumber.replace(/\s/g, "").length < 16)
      e.cardNumber = "Enter a valid 16-digit card number.";
    if (!expiry || expiry.length < 5) e.expiry = "Enter expiry date (MM/YY).";
    if (!cvv || cvv.length < 3) e.cvv = "Enter a valid CVV.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    await new Promise((r) => setTimeout(r, 1600));
    setSaving(false);
    setSuccess(true);
  };

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const selectedPlan = PLANS[planIdx];

  // ─── Success screen ────────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen bg-[#f4f5f9] flex flex-col">
        <DemoBanner />
        <div className="flex-1 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="bg-white rounded-3xl shadow-lg px-8 py-12 max-w-sm w-full text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 20 }}
            className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5"
          >
            <CheckIcon className="w-8 h-8 text-emerald-500" />
          </motion.div>
          <h2 className="text-[20px] font-bold text-gray-800 mb-2">You're all set!</h2>
          <p className="text-[13px] text-gray-500 leading-relaxed mb-6">
            Your <strong>{selectedPlan.icon} {selectedPlan.nombre}</strong> membership request
            would be confirmed here. This is a demo, so nothing was charged or sent.
          </p>
          <div className="px-4 py-3 bg-[#1a1f6c]/5 border border-[#1a1f6c]/10 rounded-xl text-[12px] text-[#1a1f6c] font-semibold">
            {selectedPlan.icon} {selectedPlan.nombre} · ${selectedPlan.precio}/month
          </div>
          <img
            src="/kinitas-logo.svg"
            alt="Kinitas"
            className="h-6 w-auto mx-auto mt-8 opacity-25"
          />
        </motion.div>
        </div>
      </div>
    );
  }

  // ─── Form ──────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#f4f5f9]">
      <DemoBanner />

      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-gray-200 px-4 sm:px-6 py-2.5">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <img src="/kinitas-logo.svg" alt="Kinitas" className="h-8 w-auto" />
          <span className="text-[11px] font-semibold text-[#1a1f6c] uppercase tracking-widest">
            New Membership
          </span>
        </div>
      </div>

      {/* Hero strip */}
      <div className="bg-[#1a1f6c] px-4 sm:px-6 py-8">
        <motion.div
          className="max-w-2xl mx-auto text-center"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
        >
          <p className="text-[11px] font-bold uppercase tracking-widest text-white/50 mb-2">
            Car Wash Membership
          </p>
          <h1 className="text-[22px] sm:text-[26px] font-bold text-white leading-tight">
            Join Kinitas & Ride Clean
          </h1>
          <p className="text-[13px] text-white/60 mt-2 max-w-md mx-auto">
            Fill out the form below and our team will confirm your membership within 24 hours.
          </p>
        </motion.div>
      </div>

      {/* Form body */}
      <div className="px-4 sm:px-6 py-6 max-w-2xl mx-auto">
        <motion.div
          className="flex flex-col gap-5"
          initial="hidden"
          animate="show"
          variants={stagger}
        >

          {/* Personal Information */}
          <SectionCard icon="🤝" title="Personal Information" sub="Required to create the membership">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Field label="Full Name *" icon={<UserIcon className="w-4 h-4" />} error={errors.nombre}>
                  <input
                    value={nombre}
                    onChange={(e) => { setNombre(e.target.value); setErrors((p) => ({ ...p, nombre: "" })); }}
                    placeholder="John Doe"
                    className={inputCls}
                  />
                </Field>
              </div>
              <Field label="Email *" icon={<EnvelopeIcon className="w-4 h-4" />} error={errors.email}
                hint="Used as your membership identifier">
                <input
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })); }}
                  placeholder="john@example.com"
                  type="email"
                  className={inputCls}
                />
              </Field>
              <Field label="Phone" icon={<PhoneIcon className="w-4 h-4" />} hint="Optional — for notifications">
                <input
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  type="tel"
                  className={inputCls}
                />
              </Field>
            </div>
          </SectionCard>

          {/* Vehicle */}
          <SectionCard icon="🚗" title="Vehicle" sub="Optional — for automatic LPR recognition">
            <Field label="License Plate" icon={<CarIcon />} error={errors.placa}
              hint="Used for automatic LPR entry at the gate">
              <input
                value={placa}
                onChange={(e) => { setPlaca(e.target.value.toUpperCase()); setErrors((p) => ({ ...p, placa: "" })); }}
                placeholder="ABC1234"
                className={`${inputCls} font-mono tracking-widest`}
                maxLength={10}
              />
            </Field>
          </SectionCard>

          {/* Membership Plan */}
          <SectionCard icon="💳" title="Membership Plan" sub="Choose the plan that fits you best">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PLANS.map((p, i) => {
                const active = planIdx === i;
                return (
                  <button key={p.nombre} onClick={() => setPlanIdx(i)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all duration-200 ${
                      active
                        ? "bg-[#1a1f6c]/5 border-[#1a1f6c]/25"
                        : "bg-gray-50 border-gray-100 hover:bg-white hover:border-gray-200"
                    }`}
                  >
                    <span className="text-[20px] flex-shrink-0">{p.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-[12px] font-bold truncate ${active ? "text-[#1a1f6c]" : "text-gray-700"}`}>
                        {p.nombre}
                      </div>
                      <div className="text-[10px] text-gray-400">${p.precio}/month</div>
                    </div>
                    <AnimatePresence>
                      {active && (
                        <motion.span
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          transition={{ duration: 0.18 }}
                          className="w-5 h-5 rounded-full bg-[#1a1f6c] flex items-center justify-center flex-shrink-0"
                        >
                          <CheckIcon className="w-3 h-3 text-white" />
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </button>
                );
              })}
            </div>
            <div
              className="mt-3 px-4 py-3 bg-[#1a1f6c]/4 border border-[#1a1f6c]/10 rounded-xl text-[11px] text-[#1a1f6c] font-semibold"
              style={{
                opacity: planVisible ? 1 : 0,
                transition: "opacity 0.13s ease",
              }}
            >
              Selected: {shownPlan.icon} {shownPlan.nombre} — ${shownPlan.precio}/month · Auto-renew ON
            </div>
          </SectionCard>

          {/* Payment */}
          <SectionCard icon="🔒" title="Payment Details" sub="Your card info is encrypted and secure">
            <div className="grid grid-cols-1 gap-4">
              <Field label="Cardholder Name" icon={<UserIcon className="w-4 h-4" />}>
                <input
                  value={cardName}
                  onChange={(e) => setCardName(e.target.value)}
                  placeholder="John Doe"
                  className={inputCls}
                />
              </Field>

              <Field label="Card Number *" icon={<CreditCardIcon className="w-4 h-4" />} error={errors.cardNumber}>
                <input
                  value={cardNumber}
                  onChange={(e) => {
                    setCardNumber(formatCardNumber(e.target.value));
                    setErrors((p) => ({ ...p, cardNumber: "" }));
                  }}
                  placeholder="0000 0000 0000 0000"
                  className={`${inputCls} font-mono tracking-widest`}
                  inputMode="numeric"
                  maxLength={19}
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Expiry *" icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                } error={errors.expiry}>
                  <input
                    value={expiry}
                    onChange={(e) => {
                      setExpiry(formatExpiry(e.target.value));
                      setErrors((p) => ({ ...p, expiry: "" }));
                    }}
                    placeholder="MM/YY"
                    className={`${inputCls} font-mono`}
                    inputMode="numeric"
                    maxLength={5}
                  />
                </Field>

                <Field label="CVV *" icon={<LockClosedIcon className="w-4 h-4" />} error={errors.cvv}>
                  <input
                    value={cvv}
                    onChange={(e) => {
                      setCvv(e.target.value.replace(/\D/g, "").slice(0, 4));
                      setErrors((p) => ({ ...p, cvv: "" }));
                    }}
                    placeholder="•••"
                    className={`${inputCls} font-mono tracking-widest`}
                    inputMode="numeric"
                    maxLength={4}
                  />
                </Field>
              </div>

              <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl">
                <LockClosedIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <p className="text-[10px] text-gray-400">
                  Demo only: card details are not sent or stored.
                </p>
              </div>
            </div>
          </SectionCard>

          {/* Submit */}
          <motion.div variants={fadeUp} className="pb-8">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className={`w-full flex items-center justify-center gap-3 py-4 rounded-2xl text-[14px] font-bold transition-all duration-300 shadow-sm active:scale-[0.99] disabled:opacity-70 ${
                saving
                  ? "bg-[#1a1f6c]/80 text-white cursor-wait"
                  : "bg-[#1a1f6c] text-white hover:bg-[#2832c2] shadow-[#1a1f6c]/20"
              }`}
            >
              {saving
                ? <><Spinner />Submitting…</>
                : <><UserIcon className="w-5 h-5" />Request Membership</>}
            </button>
            <p className="text-center text-[10px] text-gray-400 mt-3">
              By submitting, you agree to our Terms & Conditions.
            </p>
          </motion.div>

        </motion.div>
      </div>
    </div>
  );
};
